import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarSubmenu,
  useSidebarOpenState,
} from "@backstage/core-components";
import { MyGroupsSidebarItem } from "@backstage/plugin-org";
import { SidebarSearchModal } from "@backstage/plugin-search";
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from "@backstage/plugin-user-settings";
import { makeStyles } from "@material-ui/core";
import CreateComponentIcon from "@material-ui/icons/AddCircleOutline";
import CategoryIcon from "@material-ui/icons/Category";
import CloudIcon from "@material-ui/icons/Cloud";
import DataUsageIcon from "@material-ui/icons/DataUsage";
import DescriptionIcon from "@material-ui/icons/Description";
import ExtensionIcon from "@material-ui/icons/Extension";
import HomeIcon from "@material-ui/icons/Home";
import LibraryBooks from "@material-ui/icons/LibraryBooks";
import MenuIcon from "@material-ui/icons/Menu";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import GroupIcon from "@material-ui/icons/People";
import SaveIcon from "@material-ui/icons/Save";
import SearchIcon from "@material-ui/icons/Search";
import Wifi from "@material-ui/icons/Wifi";
import type { PropsWithChildren } from "react";

import LogoFull from "./LogoFull";
import LogoIcon from "./LogoIcon";



const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    height: 3 * sidebarConfig.logoHeight,
    display: "flex",
    flexFlow: "row nowrap",
    alignItems: "center",
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/"
        underline="none"
        className={classes.link}
        aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search"
        icon={<SearchIcon />}
        to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon}
          to="/"
          text="Home" />
        <SidebarItem icon={CategoryIcon}
          to="catalog"
          text="Catalog" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon}
          to="api-docs"
          text="APIs" />
        <SidebarItem icon={LibraryBooks}
          to="docs"
          text="Docs" />
        <SidebarItem icon={CreateComponentIcon}
          to="create"
          text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
      </SidebarGroup>
      <SidebarItem
        icon={OpenInNewIcon}
        text="External Links"
      >
        <SidebarSubmenu title="External Links">
          <SidebarItem target="_blank"
            icon={Wifi}
            to="https://kineticvision.sharepoint.com/sites/kinetic-vision"
            text="Intranet" />
          <SidebarItem target="_blank"
            icon={CloudIcon}
            to="https://kinetic-vision.lightning.force.com"
            text="Financial Force" />
          <SidebarItem target="_blank"
            icon={DescriptionIcon}
            to="https://kinetic-vision.atlassian.net/wiki/home"
            text="Confluence" />
          <SidebarItem target="_blank"
            icon={SaveIcon}
            to="https://kineticvision.jfrog.io"
            text="Artifactory" />
          <SidebarItem target="_blank"
            icon={DataUsageIcon}
            text="Resouring"
            to="https://resourcing.kv.k8s.kinetic-vision.com"
          />
        </SidebarSubmenu>
      </SidebarItem>
      <SidebarDivider />
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
